import { computed, nextTick, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  ContentsContext,
  Feature,
  FeedType,
  IndustryData,
  PageName,
  SearchData,
} from '@/api/tracking';
import {
  AdpDocument,
  ArticleMarkSource,
  Comment,
  Group,
  Mention,
} from '@/types';
import type { InsightSourceContext } from '@/types/insights';
import { useComment } from '@/utils/actions/comment';
import { useGroupMark } from '@/utils/actions/group-mark';
import { useMark } from '@/utils/actions/mark';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getSiteType } from '@/utils/adpDocument';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useEmitter, useStore } from '@/utils/vue';

// AdpDocumentCard（および将来のユースケース別カード）が渡す、計測・アクションに必要な情報。
export type AdpDocumentActionsContext = {
  pageName: PageName;
  feature?: Feature;
  feedType?: FeedType;
  themeId?: number;
  markSource?: ArticleMarkSource;
  rankInWholeFeed?: number;
  searchData?: SearchData;
  industryData?: IndustryData;
  trendCompanyText?: string;
  contentsContext?: ContentsContext;
  insightSourceContext?: InsightSourceContext;
  execution?: string;
  group?: Group;
  disableRelatedArticles?: boolean;
};

export const useAdpDocumentActions = (
  adpDocument: Ref<AdpDocument | undefined>,
  context: AdpDocumentActionsContext,
) => {
  const store = useStore();
  const route = useRoute();
  const emitter = useEmitter();
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const layoutVariant = useLayoutVariant();

  const getPageGroupIdParam = () => {
    let pageGroupIdParam = route.params.groupId;
    if (pageGroupIdParam instanceof Array) {
      pageGroupIdParam = pageGroupIdParam[0];
    }
    return pageGroupIdParam ? parseInt(pageGroupIdParam) : undefined;
  };

  const { createMark, removeMark } = useMark({
    markSource: context.markSource,
    trackingBaseData: {
      pageName: context.pageName,
      pageUrl: getCurrentPathForTracking(),
      feature: context.feature,
      feedType: context.feedType,
      groupId: context.group?.id,
      insightSourceContext: context.insightSourceContext,
      layoutVariant,
    },
    rankInWholeFeed: context.rankInWholeFeed,
    searchData: context.searchData,
    industryData: context.industryData,
    trendCompanyText: context.trendCompanyText,
    contentsContext: context.contentsContext,
    execution: context.execution,
  });

  const { createGroupMark } = useGroupMark({
    markSource: context.markSource,
    trackingBaseData: {
      pageName: context.pageName,
      pageUrl: getCurrentPathForTracking(),
      pageGroupId: getPageGroupIdParam(),
      feature: context.feature,
      feedType: context.feedType,
      insightSourceContext: context.insightSourceContext,
      layoutVariant,
    },
    rankInWholeFeed: context.rankInWholeFeed,
    searchData: context.searchData,
    industryData: context.industryData,
    trendCompanyText: context.trendCompanyText,
    contentsContext: context.contentsContext,
    execution: context.execution,
  });

  const { createComment, updateComment, removeComment } = useComment({
    trackingBaseData: {
      pageName: context.pageName,
      pageUrl: getCurrentPathForTracking(),
      pageGroupId: getPageGroupIdParam(),
      feature: context.feature,
      feedType: context.feedType,
      insightSourceContext: context.insightSourceContext,
      layoutVariant,
    },
    rankInWholeFeed: context.rankInWholeFeed,
    searchData: context.searchData,
    industryData: context.industryData,
    trendCompanyText: context.trendCompanyText,
    contentsContext: context.contentsContext,
    execution: context.execution,
  });

  const userInfo = computed(() => store.state.userInfo.userInfo);
  const teamInfo = computed(() => store.state.teamInfo.teamInfo);
  const enableAllianceMedia = computed(
    () => teamInfo.value?.enable_alliance_media ?? false,
  );
  const theme = computed(() =>
    store.state.themeList.themes.find(theme => theme.id === context.themeId),
  );

  const hasMyMark = computed(() => {
    return (
      adpDocument.value?.marks.some(
        mark => mark.user_id === userInfo.value?.id,
      ) ?? false
    );
  });

  const isMarkingArticle = ref(false);
  const isPostingComment = ref(false);

  const markArticle = async (markProps?: {
    tags?: string[];
    memo?: string;
  }) => {
    const doc = adpDocument.value;
    if (isMarkingArticle.value || !userInfo.value || !doc) return;
    isMarkingArticle.value = true;

    try {
      if (hasMyMark.value) {
        await removeMark({
          adpDocument: doc,
          userInfo: userInfo.value,
          afterUndo: () => {
            emitter.emit('add-article-for-article-list', doc);
            emitter.emit('article-updated', doc);
          },
        });
        emitter.emit('remove-article-for-article-list', doc);
        emitter.emit('article-updated', doc);
      } else {
        await createMark({
          adpDocument: doc,
          userInfo: userInfo.value,
          themeId: context.themeId ?? null,
          tags: markProps?.tags,
          memo: markProps?.memo,
        });
        emitter.emit('add-article-for-article-list', doc);
        emitter.emit('article-updated', doc);
      }
    } finally {
      await nextTick();
      isMarkingArticle.value = false;
    }
  };

  const groupMarkArticle = async ({
    group,
    tags,
    memo,
  }: {
    group: Group;
    tags?: string[];
    memo?: string;
  }) => {
    if (!adpDocument.value) return;

    await createGroupMark({
      adpDocument: adpDocument.value,
      group: group,
      themeId: context.themeId,
      tags: tags,
      memo: memo,
    });
    emitter.emit('article-updated', adpDocument.value);
  };

  const commentArticle = async ({
    group,
    content,
    mention,
  }: {
    group: Group;
    content: string;
    mention?: Mention;
  }) => {
    try {
      isPostingComment.value = true;
      const createdComment = await createComment({
        groupId: group.id,
        adpDocument: adpDocument.value,
        themeId: context.themeId,
        content,
        mention,
      });
      emitter.emit('article-updated', adpDocument.value);
      emitter.emit('comment-created', {
        ...createdComment,
        article: adpDocument.value,
      });
    } finally {
      isPostingComment.value = false;
    }
  };

  const editComment = async ({
    comment,
    content,
  }: {
    comment: Comment;
    content: string;
  }) => {
    if (!adpDocument.value) return;

    try {
      isPostingComment.value = true;
      const updatedComment = await updateComment({
        adpDocument: adpDocument.value,
        comment: comment,
        content: content,
      });
      emitter.emit('comment-updated', updatedComment);
      emitter.emit('article-updated', adpDocument.value);
    } finally {
      isPostingComment.value = false;
    }
  };

  const deleteComment = async (comment: Comment) => {
    const deleteAction = async () => {
      await removeComment({
        adpDocument: comment.article,
        comment: comment,
      });
      emitter.emit('comment-deleted', comment?.id);
      if (comment.article) {
        emitter.emit('article-updated', comment.article);
      }
    };
    const payload = {
      headerText: 'コメントを削除しますか？',
      bodyText: '',
      action: deleteAction,
    };
    store.commit('confirmationModal/showConfirmationModal');
    store.commit('confirmationModal/setTextAndAction', payload);
  };

  const updateIsRead = () => {
    if (!adpDocument.value) return;
    // 検索のキャッシュを更新するため
    emitter.emit('article-updated', { ...adpDocument.value, is_read: true });
  };

  const sendViewProps = computed(() => ({
    adpDocument: adpDocument.value,
    themeId: context.themeId,
    groupId: context.group?.id,
    rankInWholeFeed: context.rankInWholeFeed,
    trackingBaseData: {
      pageName: context.pageName,
      pageUrl: getCurrentPathForTracking(),
      feature: context.feature,
      feedType: context.feedType,
      layoutVariant,
    },
    searchData: context.searchData,
    industryData: context.industryData,
    siteType: getSiteType(adpDocument.value),
    description: context.trendCompanyText,
    contentsContext: context.contentsContext,
    enableAllianceMedia: enableAllianceMedia.value,
    execution: context.execution,
  }));

  // 記事リンクがクリックされたときの計測・閲覧済み反映をまとめる。
  const handleArticleLinkClicked = () => {
    updateIsRead();
    articleClickedEvent(sendViewProps.value, adpDocument.value);
  };

  const handleArticleContextMenu = (event: Event) => {
    articleContextMenuClickedEvent(
      sendViewProps.value,
      event,
      adpDocument.value,
    );
  };

  watch(
    () => adpDocument.value?.marks,
    async () => {
      if (adpDocument.value && route.name === 'search') {
        await store.dispatch('searchResults/updateArticle', adpDocument.value);
      }
    },
  );

  return {
    isMarkingArticle,
    isPostingComment,
    enableAllianceMedia,
    theme,
    markArticle,
    groupMarkArticle,
    commentArticle,
    editComment,
    deleteComment,
    handleArticleLinkClicked,
    handleArticleContextMenu,
  };
};
